/**
 * useContributions — shell-side consumer of the BFF contribution store.
 *
 * First paint hydrates from the audience-filtered snapshot GET; thereafter the
 * canonical `useEventStream` SSE subscription pushes the full view whenever the
 * store revision advances. We never re-roll `EventSource` — the shared hook owns
 * reconnect, credentials, backoff, and stale-by-data-age (StreamStatusPill reads
 * `connectionState` + `dataStale`). The server re-evaluates entitlement on every
 * hydration and SSE tick, so a revoked role stops delivering that group's items;
 * this hook simply renders whatever the server last delivered.
 */

import React from 'react';
import { useEventStream } from '@experience/shared-ui';

export interface ContributionBadge {
  pluginId: string;
  /** "count" for a user audience (exact number), "dot" for an entitlement/group. */
  mode: 'count' | 'dot';
  count: number;
  ariaLabelKey: string;
  ariaLabelParams?: Record<string, unknown>;
}

export interface ContributionMessage {
  messageKey: string;
  fallback: string;
  params?: Record<string, unknown>;
}

export interface ContributionNotification {
  itemId: string;
  pluginId: string;
  severity: 'info' | 'warning' | 'critical';
  message: ContributionMessage | null;
  action?: { url: string } | null;
  updatedAt: number;
}

export interface ContributionView {
  revision: number;
  epoch: number;
  badges: ContributionBadge[];
  needsYou: { items: ContributionNotification[]; overflow: number };
  heartbeatSeconds: number;
  staleFactor: number;
}

export interface UseContributionsResult {
  view: ContributionView | null;
  badgesByPlugin: Map<string, ContributionBadge>;
  connectionState: string;
  dataStale: boolean;
  loading: boolean;
  /** Non-fatal: e.g. no authenticated identity (401). The shell degrades to no rail. */
  error: string | null;
}

const HYDRATE_URL = '/api/v1/shell/contributions';
const STREAM_URL = '/api/v1/shell/contributions/stream';
const DEFAULT_HEARTBEAT_SECONDS = 15;
const DEFAULT_STALE_FACTOR = 3;
// Hard ceiling for the first-paint hydration GET. Without it, a slow/unreachable
// BFF leaves the rail wedged on its loading skeletons forever (the fetch never
// settles, so `loading` never flips false). With it, a stalled hydrate degrades
// to the empty/offline state — the SSE StreamStatusPill still shows reconnecting.
const HYDRATE_TIMEOUT_MS = 10000;

function isView(v: unknown): v is ContributionView {
  return !!v && typeof v === 'object' && typeof (v as any).revision === 'number';
}

export function useContributions(enabled: boolean = true): UseContributionsResult {
  const [view, setView] = React.useState<ContributionView | null>(null);
  const [loading, setLoading] = React.useState<boolean>(enabled);
  const [error, setError] = React.useState<string | null>(null);
  // Track the highest revision applied so a late GET can never clobber a newer SSE frame.
  const revisionRef = React.useRef<number>(-1);

  const applyView = React.useCallback((next: ContributionView | null) => {
    if (!isView(next)) return;
    if (next.revision < revisionRef.current) return; // monotonic: ignore stale
    revisionRef.current = next.revision;
    setView(next);
  }, []);

  // First-paint hydration (SSE only pushes on the next revision change, which can
  // be up to a heartbeat away — so fetch the current snapshot immediately).
  React.useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(HYDRATE_URL, {
          credentials: 'include',
          // Bound the call so an unresponsive BFF can't wedge the rail on skeletons.
          signal: AbortSignal.timeout(HYDRATE_TIMEOUT_MS),
        });
        if (cancelled) return;
        if (res.status === 401) {
          setError('unauthenticated');
          setLoading(false);
          return;
        }
        if (!res.ok) {
          setError(`hydrate_failed_${res.status}`);
          setLoading(false);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        applyView(data as ContributionView);
        setError(null);
      } catch (e: any) {
        // A timeout (AbortSignal) or network failure lands here — degrade to the
        // empty/offline state rather than an eternal skeleton.
        if (!cancelled) {
          const isTimeout = e?.name === 'TimeoutError' || e?.name === 'AbortError';
          setError(isTimeout ? 'hydrate_timeout' : e?.message || 'hydrate_error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, applyView]);

  const cadenceMs =
    (view?.heartbeatSeconds ?? DEFAULT_HEARTBEAT_SECONDS) * 1000;
  const staleFactor = view?.staleFactor ?? DEFAULT_STALE_FACTOR;

  const stream = useEventStream(enabled ? STREAM_URL : null, {
    events: ['contributions'],
    cadenceMs,
    staleFactor,
    // Only a real data frame advances freshness; heartbeats keep the socket
    // "connected" but must not make a frozen count read Live.
    freshnessEvents: ['contributions'],
    enabled,
  });

  // Fold each SSE 'contributions' frame (full view) into state, monotonically.
  const lastContribution = stream.lastEventByType?.contributions;
  React.useEffect(() => {
    if (lastContribution?.data) applyView(lastContribution.data as ContributionView);
  }, [lastContribution, applyView]);

  const badgesByPlugin = React.useMemo(() => {
    const m = new Map<string, ContributionBadge>();
    for (const b of view?.badges ?? []) m.set(b.pluginId, b);
    return m;
  }, [view]);

  return {
    view,
    badgesByPlugin,
    connectionState: stream.connectionState,
    dataStale: stream.dataStale,
    loading,
    error,
  };
}

export default useContributions;
